import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildPriceReviewColumns } from '../columns';
import { STAGING_SORT_KEYS } from '../schema';
import type {
  SortDirection,
  StagingRow,
  StagingSortKey,
} from '../schema';
import type { CommonResponse } from '@/types/api';

// v33 splits grid APIs into modules; AllCommunityModule provides getColumnState
// (used by readSort) — without it, calling it raises error #200.
ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<StagingRow> | undefined;
  onSortChange: (sort: {
    sortBy?: StagingSortKey;
    sortDir?: SortDirection;
  }) => void;
  onView: (row: StagingRow) => void;
  onApprove: (row: StagingRow) => void;
  onReject: (row: StagingRow) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 72,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(STAGING_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: StagingSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as StagingSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function PriceReviewGrid({
  data,
  onSortChange,
  onView,
  onApprove,
  onReject,
}: Props) {
  const columnDefs = useMemo(
    () => buildPriceReviewColumns({ onView, onApprove, onReject }),
    [onView, onApprove, onReject],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<StagingRow>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        rowHeight={52}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
